import React, { useState, forwardRef, useImperativeHandle, useCallback } from "react";
import { Table, Tag, Space, Button, Spin, Modal, Form, Input, Select, notification, Alert } from "antd";
import styled from "styled-components";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { useTranslation } from "react-i18next";
import {
  getPaymentMethods,
  createPaymentMethod,
  updatePaymentMethod,
  deletePaymentMethod,
  editCompanyBranch,
} from "../../../service/client";
import { useBranchContext } from "../../../contexts/branch-context";
import type { ColumnsType } from "antd/es/table";
import { StyledSwitch, SectionTitle, SettingItem, SettingRow, SettingContent, SettingLabel, SettingDescription } from "../../../styles/shared-components";
import { colors } from "../../../styles/theme";
import { EU_CURRENCIES, P24_SUPPORTED_CURRENCIES } from "../../../utils/currency";

export interface PaymentMethodsRef {
  hasUnconfiguredP24: () => boolean;
  showP24WarnModal: (onLeave: () => void) => void;
}

interface PaymentMethod {
  id: number;
  name: string;
  type: string;
  isActive: boolean;
  configuration?: {
    posId?: string;
    apiKey?: string;
    crcKey?: string;
    merchantId?: string;
    secretKey?: string;
    publishableKey?: string;
  };
}

const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`;

const AddButton = styled(Button)`
  border-radius: 8px;
  background: ${colors.primary};
  border-color: ${colors.primary};

  &:hover {
    background: ${colors.primaryHover} !important;
    border-color: ${colors.primaryHover} !important;
  }
`;

const tableStyles = {
  borderRadius: 8,
  overflow: 'hidden' as const,
};

const SimpleModeContainer = styled.div`
  max-width: 800px;
`;

const Przelewy24Form = styled.div`
  margin-top: 16px;
  padding: 20px;
  background: var(--color-background-secondary);
  border-radius: 8px;
  border: 1px solid ${colors.borderLight};
`;

const SaveButton = styled(Button)`
  margin-top: 16px;
  border-radius: 8px;
`;

export const PaymentMethods = forwardRef<PaymentMethodsRef>((props, ref) => {
  const { t } = useTranslation();
  const { currentBranch } = useBranchContext();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingMethod, setEditingMethod] = useState<PaymentMethod | null>(null);
  const [p24WarnModalOpen, setP24WarnModalOpen] = useState(false);
  const [onLeaveCallback, setOnLeaveCallback] = useState<(() => void) | null>(null);
  const [form] = Form.useForm();
  const [simpleForm] = Form.useForm();
  const [stripeForm] = Form.useForm();
  const queryClient = useQueryClient();
  const paymentType = Form.useWatch("type", form);

  const { data: paymentMethodsRaw, isLoading } = useQuery(
    ["/payment-methods", currentBranch?.id],
    () => getPaymentMethods({ branchId: currentBranch?.id as number }),
    {
      enabled: !!currentBranch?.id,
    }
  );

  // Sortuj metody po id (data utworzenia) aby kolejność była stała
  const paymentMethods = React.useMemo(() => {
    if (!paymentMethodsRaw) return [];
    return [...paymentMethodsRaw].sort((a, b) => a.id - b.id);
  }, [paymentMethodsRaw]);

  // Sprawdź czy są metody spoza obsługiwanych w trybie prostym (PRZELEWY24, STRIPE, OFFLINE).
  // STRIPE jest pełnoprawnym dostawcą online obok P24 — NIE wyrzuca panelu w tryb zaawansowany.
  const hasAdvancedMethods = React.useMemo(() => {
    return paymentMethods.some(m =>
      m.type !== 'PRZELEWY24' && m.type !== 'STRIPE' && m.type !== 'OFFLINE'
    );
  }, [paymentMethods]);

  // Per-branch payment flags (branch-specific, independent of other branches)
  const hasActiveOnlinePayment = currentBranch?.acceptsOnlinePayments === true;
  const hasActiveOfflinePayment = currentBranch?.acceptsOfflinePayments === true;

  // Dostawcy online to OSOBNE rekordy PaymentMethod (per-firma). Włączenie/wyłączenie
  // dostawcy = flaga isActive na jego rekordzie. Konfiguracja Stripe nigdy nie miesza
  // się z configiem P24 — to fizycznie inny wiersz w bazie.
  const p24Method = React.useMemo(
    () => paymentMethods.find(m => m.type === 'PRZELEWY24'),
    [paymentMethods]
  );
  const stripeMethod = React.useMemo(
    () => paymentMethods.find(m => m.type === 'STRIPE'),
    [paymentMethods]
  );

  const isP24Enabled = !!(p24Method && p24Method.isActive);
  const isStripeEnabled = !!(stripeMethod && stripeMethod.isActive);

  // Pobierz konfigurację Przelewy24 jeśli istnieje
  const przelewy24Config = React.useMemo(
    () => (p24Method?.configuration as any) || {},
    [p24Method]
  );
  const stripeConfig = React.useMemo(
    () => (stripeMethod?.configuration as any) || {},
    [stripeMethod]
  );

  // Populate forms with provider configs
  React.useEffect(() => {
    if (hasActiveOnlinePayment && przelewy24Config) {
      simpleForm.setFieldsValue(przelewy24Config);
    }
  }, [hasActiveOnlinePayment, przelewy24Config, simpleForm]);

  React.useEffect(() => {
    if (hasActiveOnlinePayment && stripeConfig) {
      stripeForm.setFieldsValue(stripeConfig);
    }
  }, [hasActiveOnlinePayment, stripeConfig, stripeForm]);

  const isP24Configured = useCallback(() => {
    const cfg = przelewy24Config as any;
    return !!(cfg?.merchantId && cfg?.apiKey && cfg?.crcKey);
  }, [przelewy24Config]);

  useImperativeHandle(ref, () => ({
    hasUnconfiguredP24: () => hasActiveOnlinePayment && isP24Enabled && !isP24Configured(),
    showP24WarnModal: (onLeave: () => void) => {
      setOnLeaveCallback(() => onLeave);
      setP24WarnModalOpen(true);
    },
  }));

  const { mutateAsync: createMethod } = useMutation(createPaymentMethod, {
    onSuccess: () => {
      notification.success({
        message: t("paymentMethodsSettings.methodCreated"),
        placement: "bottomRight",
      });
      queryClient.invalidateQueries(["/payment-methods"]);
      queryClient.invalidateQueries(["/company-branches"]);
      setIsModalOpen(false);
      form.resetFields();
    },
  });

  const { mutateAsync: updateMethod } = useMutation(updatePaymentMethod, {
    onSuccess: () => {
      notification.success({
        message: t("paymentMethodsSettings.methodUpdated"),
        placement: "bottomRight",
      });
      queryClient.invalidateQueries(["/payment-methods"]);
      setIsModalOpen(false);
      setEditingMethod(null);
      form.resetFields();
    },
  });

  const { mutateAsync: deleteMethod } = useMutation(deletePaymentMethod, {
    onSuccess: () => {
      notification.success({
        message: t("paymentMethodsSettings.methodDeleted"),
        placement: "bottomRight",
      });
      queryClient.invalidateQueries(["/payment-methods"]);
    },
    onError: (error: any) => {
      notification.error({
        message: t("paymentMethodsSettings.deleteError"),
        description: error?.response?.data?.error || t("paymentMethodsSettings.deleteErrorDescription"),
        placement: "bottomRight",
        duration: 5,
      });
    },
  });

  const { mutateAsync: updateBranchPaymentFlag } = useMutation(editCompanyBranch, {
    onSuccess: () => {
      queryClient.invalidateQueries(["/company-branches"]);
    },
    onError: () => {
      notification.error({
        message: t("paymentMethodsSettings.branchUpdateError"),
        placement: "bottomRight",
      });
    },
  });

  const handleOpenModal = (method?: PaymentMethod) => {
    if (method) {
      setEditingMethod(method);
      form.setFieldsValue({
        name: method.name,
        type: method.type,
        ...method.configuration,
      });
    } else {
      setEditingMethod(null);
      form.resetFields();
    }
    setIsModalOpen(true);
  };

  const handleSubmit = async () => {
    const values = await form.validateFields();
    const { name, type, apiKey, crcKey, merchantId, secretKey, publishableKey } = values;

    const body: any = {
      name,
      type,
      companyBranchId: currentBranch?.id,
    };

    if (type === "PRZELEWY24") {
      const trimmedMerchantId = merchantId?.trim();
      body.configuration = {
        posId: trimmedMerchantId,
        apiKey: apiKey?.trim(),
        crcKey: crcKey?.trim(),
        merchantId: trimmedMerchantId,
        sandbox: false,
      };
    }

    if (type === "STRIPE") {
      body.configuration = {
        secretKey: secretKey?.trim(),
        publishableKey: publishableKey?.trim() || undefined,
        sandbox: false,
      };
    }

    if (editingMethod) {
      await updateMethod({ id: editingMethod.id, body });
    } else {
      await createMethod(body);
    }
  };

  const handleDelete = (id: number) => {
    Modal.confirm({
      title: t("paymentMethodsSettings.deleteConfirmTitle"),
      onOk: () => deleteMethod(id),
    });
  };

  const handleToggleActive = async (record: PaymentMethod) => {
    await updateMethod({
      id: record.id,
      body: {
        isActive: !record.isActive,
      },
    });
  };

  const isMethodActive = (record: PaymentMethod) => {
    return record.isActive;
  };

  // Toggle dla podstawowych metod płatności per-branch
  const handleToggleBasicPayment = async (type: 'PRZELEWY24' | 'OFFLINE', enabled: boolean) => {
    if (enabled) {
      const existingCompanyMethod = paymentMethods.find(m => m.type === type);

      if (!existingCompanyMethod) {
        // Metoda nie istnieje na poziomie company — utwórz ją (jednorazowo)
        const name = type === 'PRZELEWY24' ? t("paymentMethodsSettings.onlinePaymentName") : t("paymentMethodsSettings.offlinePaymentName");
        const body: any = {
          name,
          type,
          companyBranchId: currentBranch?.id,
        };

        if (type === 'PRZELEWY24') {
          const values = simpleForm.getFieldsValue();
          if (values.merchantId || values.apiKey || values.crcKey) {
            body.configuration = {
              merchantId: values.merchantId,
              posId: values.merchantId,
              apiKey: values.apiKey,
              crcKey: values.crcKey,
              sandbox: false,
            };
          }
        }

        await createMethod(body);
      } else {
        // Metoda company już istnieje — tylko włącz dla tego brancha
        const flagField = type === 'PRZELEWY24'
          ? { acceptsOnlinePayments: true }
          : { acceptsOfflinePayments: true };
        await updateBranchPaymentFlag({ id: currentBranch!.id, data: flagField });
      }
    } else {
      // Wyłącz tylko dla tego brancha — NIE usuwaj metody company (inne branche mogą jej używać)
      const flagField = type === 'PRZELEWY24'
        ? { acceptsOnlinePayments: false }
        : { acceptsOfflinePayments: false };
      await updateBranchPaymentFlag({ id: currentBranch!.id, data: flagField });
    }
  };

  // Zapisz konfigurację Przelewy24
  const handleSavePrzelewy24Config = async () => {
    try {
      const values = await simpleForm.validateFields();
      const method = paymentMethods.find(m => m.type === 'PRZELEWY24');

      if (method) {
        const trimmedMerchantId = values.merchantId?.trim();
        await updateMethod({
          id: method.id,
          body: {
            configuration: {
              merchantId: trimmedMerchantId,
              posId: trimmedMerchantId,
              apiKey: values.apiKey?.trim(),
              crcKey: values.crcKey?.trim(),
              sandbox: false,
            },
          },
        });
      }
    } catch (error) {
      // Validation failed
    }
  };

  // Master per-branch: czy ten oddział w ogóle przyjmuje płatności online.
  // NIE tworzy żadnego dostawcy — wyboru P24/Stripe dokonuje się niżej.
  const handleToggleOnlineMaster = async (enabled: boolean) => {
    await updateBranchPaymentFlag({
      id: currentBranch!.id,
      data: { acceptsOnlinePayments: enabled },
    });
  };

  // Waluta sprzedaży online — per oddział. Przekazywana z frontu klienta do P24/Stripe
  // i używana w etykietach cen. Domyślnie PLN.
  const selectedCurrency = (currentBranch?.currency || "PLN").toUpperCase();
  const p24CurrencyUnsupported =
    isP24Enabled && !P24_SUPPORTED_CURRENCIES.includes(selectedCurrency);

  const handleCurrencyChange = async (currency: string) => {
    await updateBranchPaymentFlag({
      id: currentBranch!.id,
      data: { currency },
    });
  };

  // Per-firma: włącz/wyłącz konkretnego dostawcę online (PRZELEWY24 / STRIPE).
  // Dostawca to osobny rekord PaymentMethod; przełącznik steruje jego isActive.
  const handleToggleProvider = async (type: 'PRZELEWY24' | 'STRIPE', enabled: boolean) => {
    const method = paymentMethods.find(m => m.type === type);

    if (enabled) {
      if (!method) {
        const name = type === 'PRZELEWY24'
          ? t("paymentMethodsSettings.onlinePaymentName")
          : t("paymentMethodsSettings.stripePaymentName");
        // createPaymentMethod z companyBranchId ustawia też acceptsOnlinePayments na oddziale.
        await createMethod({ name, type, companyBranchId: currentBranch?.id } as any);
      } else {
        await updateMethod({ id: method.id, body: { isActive: true } });
        if (!hasActiveOnlinePayment) {
          await updateBranchPaymentFlag({
            id: currentBranch!.id,
            data: { acceptsOnlinePayments: true },
          });
        }
      }
    } else if (method) {
      await updateMethod({ id: method.id, body: { isActive: false } });
    }
  };

  // Zapisz konfigurację Stripe (osobny rekord, osobny formularz).
  const handleSaveStripeConfig = async () => {
    try {
      const values = await stripeForm.validateFields();
      const method = paymentMethods.find(m => m.type === 'STRIPE');

      if (method) {
        await updateMethod({
          id: method.id,
          body: {
            configuration: {
              secretKey: values.secretKey?.trim(),
              publishableKey: values.publishableKey?.trim() || undefined,
              sandbox: false,
            },
          },
        });
      }
    } catch (error) {
      // Validation failed
    }
  };

  const columns: ColumnsType<PaymentMethod> = [
    {
      title: t("paymentMethodsSettings.columnName"),
      dataIndex: "name",
      key: "name",
    },
    {
      title: t("paymentMethodsSettings.columnType"),
      dataIndex: "type",
      key: "type",
      render: (type: string) => {
        const color = type === "PRZELEWY24" ? "green" : type === "STRIPE" ? "purple" : "red";
        return <Tag color={color}>{type}</Tag>;
      },
    },
    {
      title: t("paymentMethodsSettings.columnMerchantId"),
      dataIndex: ["configuration", "merchantId"],
      key: "merchantId",
      render: (merchantId: string, record: PaymentMethod) =>
        record.type === "PRZELEWY24" ? merchantId || "-" : "-",
    },
    {
      title: "API Key",
      dataIndex: ["configuration", "apiKey"],
      key: "apiKey",
      render: (apiKey: string, record: PaymentMethod) =>
        record.type === "PRZELEWY24" ? (apiKey ? "••••••••" : "-") : "-",
    },
    {
      title: "CRC Key",
      dataIndex: ["configuration", "crcKey"],
      key: "crcKey",
      render: (crcKey: string, record: PaymentMethod) =>
        record.type === "PRZELEWY24" ? (crcKey ? "••••••••" : "-") : "-",
    },
    {
      title: t("paymentMethodsSettings.columnActive"),
      key: "active",
      width: 100,
      render: (_: any, record: PaymentMethod) => (
        <StyledSwitch
          checked={isMethodActive(record)}
          onChange={() => handleToggleActive(record)}
        />
      ),
    },
    {
      title: t("paymentMethodsSettings.columnActions"),
      key: "actions",
      render: (_: any, record: PaymentMethod) => (
        <Space size="middle">
          <Button type="link" size="small" onClick={() => handleOpenModal(record)}>
            {t("paymentMethodsSettings.edit")}
          </Button>
          <Button type="link" size="small" danger onClick={() => handleDelete(record.id)}>
            {t("paymentMethodsSettings.delete")}
          </Button>
        </Space>
      ),
    },
  ];

  if (!currentBranch) {
    return <Spin size="large" />;
  }

  return (
    <div>
      {!hasAdvancedMethods ? (
        // Tryb prosty - tylko switche dla podstawowych metod
        <>
          <SectionTitle style={{ marginBottom: 24 }}>{t("paymentMethodsSettings.title")}</SectionTitle>
          <SimpleModeContainer>
            <SettingItem>
              <SettingRow>
                <StyledSwitch
                  checked={hasActiveOfflinePayment}
                  onChange={(checked) => handleToggleBasicPayment('OFFLINE', checked)}
                  disabled={isLoading}
                />
                <SettingContent>
                  <SettingLabel>{t("paymentMethodsSettings.offlinePaymentLabel")}</SettingLabel>
                  <SettingDescription>
                    {t("paymentMethodsSettings.offlinePaymentDescription")}
                  </SettingDescription>
                </SettingContent>
              </SettingRow>
            </SettingItem>

            <SettingItem>
              <SettingRow>
                <StyledSwitch
                  checked={hasActiveOnlinePayment}
                  onChange={(checked) => handleToggleOnlineMaster(checked)}
                  disabled={isLoading}
                />
                <SettingContent>
                  <SettingLabel>{t("paymentMethodsSettings.onlinePaymentLabel")}</SettingLabel>
                  <SettingDescription>
                    {t("paymentMethodsSettings.onlinePaymentDescription")}
                  </SettingDescription>
                </SettingContent>
              </SettingRow>
            </SettingItem>

            {hasActiveOnlinePayment && (
              <Przelewy24Form>
                <SectionTitle style={{ fontSize: 14, marginBottom: 16 }}>
                  {t("paymentMethodsSettings.providerSectionTitle")}
                </SectionTitle>

                {/* === Waluta sprzedaży online === */}
                <SettingItem>
                  <SettingContent style={{ width: "100%" }}>
                    <SettingLabel>{t("paymentMethodsSettings.currencyLabel")}</SettingLabel>
                    <SettingDescription>
                      {t("paymentMethodsSettings.currencyDescription")}
                    </SettingDescription>
                    <Select
                      value={selectedCurrency}
                      onChange={handleCurrencyChange}
                      disabled={isLoading}
                      style={{ minWidth: 240, marginTop: 8 }}
                      options={EU_CURRENCIES.map((c) => ({
                        value: c.code,
                        label: `${c.label} — ${c.symbol}`,
                      }))}
                    />
                    {p24CurrencyUnsupported && (
                      <Alert
                        type="warning"
                        showIcon
                        style={{ marginTop: 12 }}
                        message={t("paymentMethodsSettings.currencyP24WarningTitle")}
                        description={t("paymentMethodsSettings.currencyP24WarningText", {
                          currency: selectedCurrency,
                          supported: P24_SUPPORTED_CURRENCIES.join(", "),
                        })}
                      />
                    )}
                  </SettingContent>
                </SettingItem>

                {/* === Przelewy24 === */}
                <SettingItem>
                  <SettingRow>
                    <StyledSwitch
                      checked={isP24Enabled}
                      onChange={(checked) => handleToggleProvider('PRZELEWY24', checked)}
                      disabled={isLoading}
                    />
                    <SettingContent>
                      <SettingLabel>Przelewy24</SettingLabel>
                    </SettingContent>
                  </SettingRow>
                </SettingItem>

                {isP24Enabled && (
                  <div style={{ marginTop: 12, marginBottom: 24 }}>
                    <div style={{
                      padding: '12px 16px',
                      background: 'rgba(24, 144, 255, 0.08)',
                      borderRadius: 6,
                      border: '1px solid rgba(24, 144, 255, 0.2)',
                      marginBottom: 16,
                      fontSize: 13,
                      lineHeight: 1.6,
                      color: 'var(--color-text-secondary)',
                    }}>
                      <strong style={{ color: 'var(--color-text-primary)' }}>{t("paymentMethodsSettings.howToGetConfigData")}</strong>
                      <ol style={{ margin: '8px 0 0', paddingLeft: 18 }}>
                        <li>
                          {t("paymentMethodsSettings.step1CreateAccount")}{' '}
                          <a
                            href="https://panel.przelewy24.pl/rejestracja.php?a=assign_partner&idp=183535"
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ fontWeight: 600 }}
                          >
                            {t("paymentMethodsSettings.link")}
                          </a>{' '}
                          ({t("paymentMethodsSettings.step1CommissionStart")} <strong>1.5%</strong> {t("paymentMethodsSettings.step1CommissionEnd")})
                        </li>
                        <li>{t("paymentMethodsSettings.step2ActivateAccount")}</li>
                        <li>{t("paymentMethodsSettings.step3GoToStart")} <strong>{t("paymentMethodsSettings.step3GoToPath")}</strong></li>
                        <li>{t("paymentMethodsSettings.step4CopyStart")} <strong>{t("paymentMethodsSettings.step4MerchantId")}</strong>, <strong>{t("paymentMethodsSettings.step4ApiKey")}</strong> {t("paymentMethodsSettings.step4And")} <strong>{t("paymentMethodsSettings.step4CrcKey")}</strong> {t("paymentMethodsSettings.step4Paste")}</li>
                      </ol>
                    </div>

                    <Form form={simpleForm} layout="vertical" autoComplete="off">
                      <Form.Item
                        name="merchantId"
                        label={t("paymentMethodsSettings.merchantIdLabel")}
                        rules={[{ required: true, message: t("paymentMethodsSettings.fieldRequired") }]}
                      >
                        <Input placeholder={t("paymentMethodsSettings.merchantIdPlaceholder")} autoComplete="new-password" />
                      </Form.Item>

                      <Form.Item
                        name="apiKey"
                        label={t("paymentMethodsSettings.apiKeyLabel")}
                        rules={[{ required: true, message: t("paymentMethodsSettings.fieldRequired") }]}
                      >
                        <Input.Password placeholder={t("paymentMethodsSettings.apiKeyPlaceholder")} autoComplete="new-password" />
                      </Form.Item>

                      <Form.Item
                        name="crcKey"
                        label={t("paymentMethodsSettings.crcKeyLabel")}
                        rules={[{ required: true, message: t("paymentMethodsSettings.fieldRequired") }]}
                      >
                        <Input.Password placeholder={t("paymentMethodsSettings.crcKeyPlaceholder")} autoComplete="new-password" />
                      </Form.Item>
                    </Form>
                    <SaveButton type="primary" onClick={handleSavePrzelewy24Config}>
                      {t("paymentMethodsSettings.saveConfig")}
                    </SaveButton>
                  </div>
                )}

                {/* === Stripe === */}
                <SettingItem>
                  <SettingRow>
                    <StyledSwitch
                      checked={isStripeEnabled}
                      onChange={(checked) => handleToggleProvider('STRIPE', checked)}
                      disabled={isLoading}
                    />
                    <SettingContent>
                      <SettingLabel>Stripe</SettingLabel>
                    </SettingContent>
                  </SettingRow>
                </SettingItem>

                {isStripeEnabled && (
                  <div style={{ marginTop: 12 }}>
                    <SectionTitle style={{ fontSize: 14, marginBottom: 12 }}>
                      {t("paymentMethodsSettings.stripeConfigTitle")}
                    </SectionTitle>

                    <div style={{
                      padding: '12px 16px',
                      background: 'rgba(99, 91, 255, 0.08)',
                      borderRadius: 6,
                      border: '1px solid rgba(99, 91, 255, 0.2)',
                      marginBottom: 16,
                      fontSize: 13,
                      lineHeight: 1.6,
                      color: 'var(--color-text-secondary)',
                    }}>
                      <strong style={{ color: 'var(--color-text-primary)' }}>{t("paymentMethodsSettings.stripeHowTo")}</strong>
                      <ol style={{ margin: '8px 0 0', paddingLeft: 18 }}>
                        <li>
                          {t("paymentMethodsSettings.stripeStep1")}{' '}
                          <a href="https://dashboard.stripe.com/apikeys" target="_blank" rel="noopener noreferrer" style={{ fontWeight: 600 }}>
                            dashboard.stripe.com/apikeys
                          </a>
                        </li>
                        <li>{t("paymentMethodsSettings.stripeStep2")}</li>
                        <li>
                          {t("paymentMethodsSettings.stripeStep3")}{' '}
                          <code>https://s1.proste.menu/v1/common_api/stripe_webhook/{stripeMethod?.id ?? '<id>'}</code>
                        </li>
                      </ol>
                    </div>

                    <Form form={stripeForm} layout="vertical" autoComplete="off">
                      <Form.Item
                        name="secretKey"
                        label={t("paymentMethodsSettings.stripeSecretKeyLabel")}
                        rules={[{ required: true, message: t("paymentMethodsSettings.fieldRequired") }]}
                      >
                        <Input.Password placeholder="sk_live_..." autoComplete="new-password" />
                      </Form.Item>

                      <Form.Item
                        name="publishableKey"
                        label={t("paymentMethodsSettings.stripePublishableKeyLabel")}
                      >
                        <Input placeholder="pk_live_..." autoComplete="new-password" />
                      </Form.Item>
                    </Form>
                    <SaveButton type="primary" onClick={handleSaveStripeConfig}>
                      {t("paymentMethodsSettings.saveConfig")}
                    </SaveButton>
                  </div>
                )}
              </Przelewy24Form>
            )}
          </SimpleModeContainer>
        </>
      ) : (
        // Tryb zaawansowany - pełna tabela z możliwością dodawania
        <>
          <HeaderRow>
            <SectionTitle style={{ margin: 0 }}>{t("paymentMethodsSettings.title")}</SectionTitle>
            <AddButton type="primary" onClick={() => handleOpenModal()}>
              {t("paymentMethodsSettings.addMethod")}
            </AddButton>
          </HeaderRow>
          <Table
            columns={columns}
            dataSource={paymentMethods}
            loading={isLoading}
            rowKey="id"
            pagination={false}
            style={tableStyles}
          />
        </>
      )}

      {/* Modal dodawania/edycji metod - tylko w trybie zaawansowanym */}
      {hasAdvancedMethods && (
        <Modal
          title={editingMethod ? t("paymentMethodsSettings.editModalTitle") : t("paymentMethodsSettings.addModalTitle")}
          open={isModalOpen}
          onOk={handleSubmit}
          onCancel={() => {
            setIsModalOpen(false);
            setEditingMethod(null);
            form.resetFields();
          }}
          width={600}
        >
          <Form form={form} layout="vertical" style={{ marginTop: 24 }}>
            <Form.Item
              name="name"
              label={t("paymentMethodsSettings.nameLabel")}
              rules={[{ required: true, message: t("paymentMethodsSettings.nameRequired") }]}
            >
              <Input placeholder={t("paymentMethodsSettings.namePlaceholder")} />
            </Form.Item>

            <Form.Item
              name="type"
              label={t("paymentMethodsSettings.typeLabel")}
              rules={[{ required: true, message: t("paymentMethodsSettings.typeRequired") }]}
            >
              <Select placeholder={t("paymentMethodsSettings.typePlaceholder")}>
                <Select.Option value="PRZELEWY24">Przelewy24</Select.Option>
                <Select.Option value="STRIPE">Stripe</Select.Option>
                <Select.Option value="OFFLINE">Offline</Select.Option>
              </Select>
            </Form.Item>

            {paymentType === "PRZELEWY24" && (
              <>
                <Form.Item
                  name="merchantId"
                  label={t("paymentMethodsSettings.merchantIdPosLabel")}
                  rules={[{ required: true, message: t("paymentMethodsSettings.merchantIdRequired") }]}
                >
                  <Input placeholder={t("paymentMethodsSettings.merchantIdPlaceholder")} />
                </Form.Item>

                <Form.Item
                  name="apiKey"
                  label="API Key"
                  rules={[{ required: true, message: t("paymentMethodsSettings.apiKeyRequired") }]}
                >
                  <Input.Password placeholder="API Key" />
                </Form.Item>

                <Form.Item
                  name="crcKey"
                  label="CRC Key"
                  rules={[{ required: true, message: t("paymentMethodsSettings.crcKeyRequired") }]}
                >
                  <Input.Password placeholder="CRC Key" />
                </Form.Item>
              </>
            )}

            {paymentType === "STRIPE" && (
              <>
                <div style={{
                  padding: '12px 16px',
                  background: 'rgba(99, 91, 255, 0.08)',
                  borderRadius: 6,
                  border: '1px solid rgba(99, 91, 255, 0.2)',
                  marginBottom: 16,
                  fontSize: 13,
                  lineHeight: 1.6,
                  color: 'var(--color-text-secondary)',
                }}>
                  <strong style={{ color: 'var(--color-text-primary)' }}>{t("paymentMethodsSettings.stripeHowTo")}</strong>
                  <ol style={{ margin: '8px 0 0', paddingLeft: 18 }}>
                    <li>
                      {t("paymentMethodsSettings.stripeStep1")}{' '}
                      <a href="https://dashboard.stripe.com/apikeys" target="_blank" rel="noopener noreferrer" style={{ fontWeight: 600 }}>
                        dashboard.stripe.com/apikeys
                      </a>
                    </li>
                    <li>{t("paymentMethodsSettings.stripeStep2")}</li>
                    <li>
                      {t("paymentMethodsSettings.stripeStep3")}{' '}
                      <code>https://s1.proste.menu/v1/common_api/stripe_webhook/&lt;id&gt;</code>
                    </li>
                  </ol>
                </div>

                <Form.Item
                  name="secretKey"
                  label={t("paymentMethodsSettings.stripeSecretKeyLabel")}
                  rules={[{ required: true, message: t("paymentMethodsSettings.fieldRequired") }]}
                >
                  <Input.Password placeholder="sk_live_..." autoComplete="new-password" />
                </Form.Item>


                <Form.Item
                  name="publishableKey"
                  label={t("paymentMethodsSettings.stripePublishableKeyLabel")}
                >
                  <Input placeholder="pk_live_... (opcjonalnie)" autoComplete="new-password" />
                </Form.Item>
              </>
            )}
          </Form>
        </Modal>
      )}

      {/* Modal ostrzeżenia o brakującej konfiguracji Przelewy24 */}
      <Modal
        title={t("paymentMethodsSettings.warnModalTitle")}
        open={p24WarnModalOpen}
        closable={false}
        maskClosable={false}
        footer={[
          <Button
            key="disable"
            onClick={() => {
              setP24WarnModalOpen(false);
              handleToggleBasicPayment('PRZELEWY24', false);
              if (onLeaveCallback) {
                onLeaveCallback();
                setOnLeaveCallback(null);
              }
            }}
          >
            {t("paymentMethodsSettings.disableOnlinePayments")}
          </Button>,
          <Button
            key="continue"
            type="primary"
            onClick={() => {
              setP24WarnModalOpen(false);
              setOnLeaveCallback(null);
            }}
          >
            {t("paymentMethodsSettings.continueConfig")}
          </Button>,
        ]}
        width={480}
        centered
      >
        <p style={{ margin: "16px 0" }}>
          {t("paymentMethodsSettings.warnModalTextStart")} <strong>{t("paymentMethodsSettings.warnModalTextBold")}</strong>.
        </p>
        <p style={{ color: "var(--color-text-secondary)", fontSize: 13 }}>
          {t("paymentMethodsSettings.warnModalHint")}
        </p>
      </Modal>
    </div>
  );
});
